/* eslint-disable react-hooks/exhaustive-deps */
import { Button, ButtonVariant, Icon, Popover, Position } from "@blueprintjs/core"
import { MouseEvent, useEffect, useRef, useState } from "react"
import { WINDOW_WIDTH } from "src/libs/interfaces/layouts"
import FieldInput, { SCALAR_TYPES } from "src/libs/scalars"
import { showToast } from "src/libs/useful"
import { __ } from "src/libs/utilities"
import { useSyncState } from "src/modules/pe-basic-module/hooks/useSyncState"
import { ALIGN, DEFAULT_SECTION_PADDING, HISTORY_TYPE, IHistoryStep, ILanding, ILandingSection, IScreenModes, LANDING_EDIT_MODE, OVERFLOW_TYPE } from "src/modules/pe-landing-module/data/interfaces"
import { ILandingConsoleStore, ILandingStore, useLandingConsoleStore, useLandingStore } from "src/modules/pe-landing-module/data/store"
import { getDimensionNumber } from "src/modules/pe-landing-module/data/utils/getDimensionNumber"
import usePosition from "src/modules/pe-landing-module/hooks/usePosition"
import { getLandingContainer } from "../../utils/getLandingContainer"
import updateSection from "./utils/updateSection"

/* Protopia Ecosystem component */
export interface IRotateHandlerProps {
   i: number
   section: ILandingSection
}
const SectionHeightHandler = (props: IRotateHandlerProps): JSX.Element => {
    const ref           = useRef(null)
    const [section, setSection] = useState<ILandingSection>(props.section)
    const settings      = useLandingConsoleStore((state:ILandingConsoleStore) => state.settings ) 
    const currentDimensionEdit:  WINDOW_WIDTH   = useLandingConsoleStore((state:ILandingConsoleStore) => state.currentDimensionEdit)
    const sizes: any    = useSyncState<DOMRect>({} as DOMRect)
    const {position, }  = usePosition( section ) 
    const height        = useSyncState<number>(0)
    
    useEffect(() => {
        const unsubscribe = useLandingStore.subscribe(
            ( state: ILandingStore ) => state.currentLanding,
            ( currentLanding:ILanding ) => { 
                const sec: ILandingSection = currentLanding.sections[ props.i ]
                           
                if( JSON.stringify( sec ) !== JSON.stringify( section ) ) { 
                    setSection(() => {
                        return sec
                    })
                }
            }, 
            {} 
        )
        return () => {
            unsubscribe();
        };
    }, [])

    const startDragPos  = useSyncState<any>({})
    const preHeight     = useSyncState<number>(0)
    const isPress       = useSyncState<boolean>(false)
    
    const onDragStart = ( evt: MouseEvent) => { 
        (evt as any).dataTransfer.setDragImage(evt.target, window.outerWidth, window.outerHeight)
        const shifter = (ref.current! as any).getBoundingClientRect()
        const landing = getLandingContainer().scrollTop
        startDragPos.set({
            top: evt.clientY - shifter.top - landing
        })
        isPress.set(true)
        evt.stopPropagation() 
        //evt.preventDefault()
    }
    const onDrag = (evt: MouseEvent) => { 
        const landingTop = getLandingContainer().scrollTop
        const target = evt.target as HTMLDivElement   
        const sectionCont = target.closest(".landing-section-container.landing-gap[data-type]") as any
        const sectionLand = sectionCont.querySelector(".landing-section")
        sectionCont!.style.zIndex = 100
        const secBounds = sectionCont?.getBoundingClientRect()!
        const dataCont = sectionCont?.querySelector(`.landing-section-container`)
        const dataHeight = dataCont?.getBoundingClientRect().height || 0
        const top = evt.clientY - startDragPos.get().top - (secBounds?.top || 0) - landingTop
        if(top >= dataHeight + ( props.section.paddingTop || DEFAULT_SECTION_PADDING ) + ( props.section.paddingBottom || DEFAULT_SECTION_PADDING)) {
            target.style.top  = top + "px"
            sectionLand.style.minHeight = top + "px"
            preHeight.set( top )
        }
        evt.stopPropagation() 
        evt.preventDefault() 
    }
    const onDragEnd = (evt: MouseEvent) => { 
        const landingTop = getLandingContainer().scrollTop
        const target = evt.target as HTMLDivElement   
        const sectionCont = target.closest(".landing-section-container.landing-gap[data-type]") as any
        const sectionLand = sectionCont.querySelector(".landing-section")
        sectionCont!.style.zIndex = position.z || props.i 
        const secBounds = sectionCont?.getBoundingClientRect()!
        const top = evt.clientY - startDragPos.get().top - (secBounds?.top || 0) - landingTop
        if(top >= 0) {
            target.style.top  = top + "px"
            updateSectionHeight( top )
        }
        isPress.set(false)
        evt.stopPropagation() 
        evt.preventDefault()
    }
    const updateSectionHeight = (height: number ) => {
        let newVal: ILandingSection, 
            historyStep: IHistoryStep = { 
                type: HISTORY_TYPE.SECTION, 
                title: __("Update section's height"), 
                prevData: props.section,
                data: null,
            } 
        if(currentDimensionEdit === WINDOW_WIDTH.PANEL) {
            newVal = { 
                ...props.section, 
                height: height === 0 ? "" : height 
            }
        }
        else {
            let dimentions: IScreenModes = !!props.section.dimentions ? props.section.dimentions : {}
            dimentions[currentDimensionEdit]  = !!dimentions[currentDimensionEdit] ? dimentions[currentDimensionEdit] : {}
            dimentions[currentDimensionEdit].height = height === 0 ? "" : height
            newVal = { 
                ...props.section, 
                dimentions,
            }
        }
        historyStep.data = newVal
        if(
            updateSection( newVal, historyStep )
        )
            showToast({message: __("Update section height")})
    }
    const getHeight = ():number => {
        const shifter = (ref.current as any)  
        const parent = shifter?.parentElement
        const parentHeight = parent?.offsetHeight || 0 
        return Math.max( parentHeight, getDimensionNumber( position?.height ) )
    }
    const onVerticalAlign = () => {
        const aligns = Object.values(ALIGN)
        const curr: ALIGN = props.section.vertical_align || ALIGN.MIDDLE
        const curNext = ( aligns.findIndex((a) => a === curr ) + 1 ) % aligns.length 

        const newVal = { ...props.section, vertical_align: aligns[curNext] }
        updateSection(
            newVal,
            { 
                type: HISTORY_TYPE.SECTION, 
                title: __("Update section's vertical align"), 
                prevData: props.section,
                data: newVal,
            }
        )
    }
    const onOverflow = () => {
        const overflow = Object.values(OVERFLOW_TYPE)
        const curr: OVERFLOW_TYPE = props.section.overflow || OVERFLOW_TYPE.HIDDEN
        const curNext = ( overflow.findIndex((a) => a === curr ) + 1 ) % overflow.length 

        const newVal = { ...props.section, overflow: overflow[curNext] }
        updateSection(
            newVal,
            { 
                type: HISTORY_TYPE.SECTION, 
                title: __("Update section's overflow"), 
                prevData: props.section,
                data: newVal,
            }
        )
    }
    useEffect(() => { 
        if( props.section.isHidden || !settings.linesAndHandlers || !settings.sectionHeight ||  sizes.get().width < WINDOW_WIDTH.TABLET ) {
            return
        }
        
        const rect = ref.current 
            ? 
            (ref.current as any).closest(".landing-section-container").querySelector(".landing-section")?.getBoundingClientRect() || {}
            : 
            {}
        sizes.set( rect ) 
        height.set( getDimensionNumber( 
            !!position.height
                ?
                position.height
                :
                props.section.height 
        ))
        
        const shifter = (ref.current! as any)
        shifter.addEventListener("dragstart", onDragStart) 
        shifter.addEventListener("drag", onDrag) 
        shifter.addEventListener("dragend", onDragEnd)  

        //дожидаемся изменения размеров, они происходят с задержкой
        setTimeout(() => {            
            //console.log(getHeight())            
            preHeight.set( getHeight() )
            shifter.style.top = (getHeight() - 1) + "px"   
        }, 200)

        return () => {
            shifter.removeEventListener("dragstart", onDragStart) 
            shifter.removeEventListener("drag", onDrag) 
            shifter.removeEventListener("dragend", onDragEnd) 
        }
    }, [ position, settings.linesAndHandlers ])
    
    if(props.section.isHidden || !settings.linesAndHandlers || !settings.sectionHeight ) {
        return <></> 
    } 
    const ph = preHeight.get() 
    return <>
            <div 
                className='section-height-handler pe-surface'  
                draggable
                ref={ref}
                style={{
                    height: getHeight()
                }}
            >
                <div className="dot-cont">
                    <Popover
                        position={Position.TOP_LEFT}
                        interactionKind="click"
                        className=" "
                        content={
                            <div className="px-4 pb-3 width-400 d-flex">
                                <FieldInput
                                    subtitle={__("Section height")}
                                    type={SCALAR_TYPES.NUMBER}
                                    value={ph}
                                    noDefault={false}
                                    editable
                                    notClear
                                    onChange={(value: number) => preHeight.set(value)}
                                />
                                <Button variant={ButtonVariant.MINIMAL} className="ml-3 mt-5 px-2 mb-2" onClick={() => updateSectionHeight( "100dvh" ) }>
                                    <div className="px-2 small text-nowrap">
                                        {__("Full screen")}
                                    </div>
                                </Button>
                                <Button variant={ButtonVariant.MINIMAL} className="ml-1 mt-5 px-2 mb-2" onClick={() => updateSectionHeight(0) }>
                                    <div className="px-2 small text-nowrap">
                                        {__("Reset")}
                                    </div>
                                </Button>
                                <Button variant={ButtonVariant.MINIMAL} className="ml-1 mt-5 mb-2" onClick={() => updateSectionHeight(ph)}>
                                    <Icon icon={ph !== getHeight() ? "tick" : "blank"} intent="danger" />
                                </Button>
                            </div>
                        }
                    >
                        <div className="dot">{ 
                            !isPress.get() 
                                ? 
                                height.get()
                                : 
                                ph 
                            }</div>
                    </Popover>
                    <div 
                        className="dot-2" 
                        title={ __("Vertical align") + ": " + __(props.section.vertical_align || ALIGN.MIDDLE).toUpperCase() } 
                        onClick={ onVerticalAlign }
                    >
                        <img src={`/assets/img/landing/vertical-align-${ props.section.vertical_align || ALIGN.MIDDLE }.svg`} alt="" />
                    </div>
                    <div 
                        className="dot-3" 
                        title={__( "Overflow " + props.section.overflow || OVERFLOW_TYPE.HIDDEN ).toUpperCase() } 
                        onClick={ onOverflow }
                    >
                        <img src={`/assets/img/landing/overflow_${ props.section.overflow || OVERFLOW_TYPE.HIDDEN }.svg`} alt="" />
                    </div>
                </div> 
            </div>
            <div className="current-dimentions text-nowrap ">{ph >= 0 ? ph : 0 }</div>
        </>
    }
export default SectionHeightHandler